import { formatISO } from "date-fns";

import { FormData } from "../../core/types";

/**
 * Error class for field transformation failures
 */
class FieldTransformError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "FieldTransformError";
  }

  static create(actualValue: unknown): FieldTransformError {
    const valueStr =
      typeof actualValue === "object" && actualValue !== null
        ? JSON.stringify(actualValue)
        : String(actualValue);
    return new FieldTransformError(
      `Unknown field structure for transformation: ${valueStr}`
    );
  }
}

/**
 * Defines how a field should be included in the mapping
 */
export type FieldInclusionStrategy =
  | "truthy" // Include only if field has a truthy value (for creates)
  | "exists" // Include if field exists in FormData, even if undefined (for updates)
  | "always"; // Always include field (for required fields)

/**
 * Configuration for mapping a single field from FormData to API format
 */
export interface FieldMappingConfig {
  /** The field key in FormData */
  sourceKey: keyof FormData;
  /** The field key in the target API object (defaults to sourceKey) */
  targetKey?: string;
  /** When to include this field in the output */
  inclusion: FieldInclusionStrategy;
  /** Optional transformation function to apply to the field value */
  transform?: (value: unknown) => unknown;
  /** Whether this field is required (for validation) */
  required?: boolean;
}

/**
 * Pre-defined transformations for common field types
 */
export const FieldTransforms = {
  /** Converts Date objects to ISO date strings, passes through strings */
  dateToISOString: (value: unknown): string | undefined => {
    if (value instanceof Date) {
      return formatISO(value, { representation: "date" });
    }
    return value as string;
  },

  /** Identity transform - passes value through as-is */
  identity: (value: unknown): unknown => value,

  /** Ensures boolean values are properly typed */
  boolean: (value: unknown): boolean => Boolean(value),
} as const;

const transformFieldValue = (value: unknown): unknown => {
  if (!value || typeof value !== "object") {
    return value;
  }

  const obj = value as Record<string, unknown>;

  if (obj.type === "StaticEnum" && "value" in obj && "label" in obj) {
    return {
      id: obj.value,
      displayValue: obj.label,
    };
  }

  if ("type" in obj && "kind" in obj && "value" in obj) {
    return {
      type: obj.type,
      kind: obj.kind,
      value: obj.value,
    };
  }

  if ("type" in obj && "value" in obj && !("kind" in obj)) {
    return {
      type: obj.type,
      value: obj.value,
    };
  }

  if ("label" in obj && "value" in obj && !("type" in obj)) {
    return obj.value;
  }

  console.warn(`[transformFieldValue] Unknown structure for field value:`, obj);
  throw FieldTransformError.create(value);
};

const CommonFieldMapping: FieldMappingConfig[] = [
  { sourceKey: "title", inclusion: "always", required: true },
  { sourceKey: "description", inclusion: "truthy" },
  {
    sourceKey: "advisor",
    inclusion: "always",
    required: true,
    transform: transformFieldValue,
  },
  {
    sourceKey: "taskStatus",
    inclusion: "always",
    required: true,
    transform: transformFieldValue,
  },
  {
    sourceKey: "taskType",
    inclusion: "always",
    required: true,
    transform: transformFieldValue,
  },
  {
    sourceKey: "taskPriority",
    inclusion: "always",
    required: true,
    transform: transformFieldValue,
  },
  { sourceKey: "description", inclusion: "truthy" },
  {
    sourceKey: "dueDate",
    inclusion: "truthy",
    transform: FieldTransforms.dateToISOString,
  },
  {
    sourceKey: "custodian",
    inclusion: "truthy",
    transform: transformFieldValue,
  },
  { sourceKey: "amount", inclusion: "truthy" },
  {
    sourceKey: "directFollowupAgreement",
    inclusion: "exists",
    transform: FieldTransforms.boolean,
  },
  {
    sourceKey: "serviceTeamId",
    inclusion: "truthy",
    transform: transformFieldValue,
  },
  {
    sourceKey: "client",
    inclusion: "truthy",
    transform: transformFieldValue,
  },
  {
    sourceKey: "assignee",
    inclusion: "truthy",
    transform: transformFieldValue,
  },
  { sourceKey: "fromAccount", inclusion: "truthy" },
  { sourceKey: "toAccount", inclusion: "truthy" },
];

/**
 * Field mappings for create operations (truthy values only for optional fields)
 * Special handling for boolean fields to preserve their "exists" strategy
 */
export const CreateFieldMappings: FieldMappingConfig[] = CommonFieldMapping.map(
  (config) => ({
    ...config,
    inclusion: config.required
      ? "always"
      : config.sourceKey === "directFollowupAgreement"
      ? "exists" // Preserve "exists" for boolean fields to include false values
      : "truthy",
  })
);

/**
 * Field mappings for update operations (only include fields that are present in form data)
 * All fields use "exists" strategy to avoid including fields that weren't explicitly provided
 */
export const UpdateFieldMappings: FieldMappingConfig[] = CommonFieldMapping.map(
  (config) => ({
    ...config,
    inclusion: "exists",
    required: false, // No required validation for updates
  })
);
